import * as React from 'react';
import { TournamentAvailabilityInbox } from 'Src/views/GenericView/AdminSUView/HighLevelTournaments/TournamentAvailability/TournamentAvailabilityInbox';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Widget } from '../../../../components/Widget/Widget';
import { Link, useHistory } from 'react-router-dom';
import './Dashboard.css';
import { TipOfTheDayWidget } from './TipOfTheDayWidget';
import { WhatsNewWidget } from './WhatsNewWidget';
import { getSchoolDashboard } from 'Src/helpers/service/admin/dashboard';
import { ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import { getEventCommunicationsDashboard } from 'Src/helpers/service/eventCommunications';

function getEventNameForSchool(event, schoolId) {
  if (event.generatedNames && event.generatedNames[schoolId]) return event.generatedNames[schoolId];
  if (event.generatedNames && event.generatedNames.official) return event.generatedNames.official;
  return event.name || 'Unnamed event';
}

export const DEFAULT_WIDGET_ORDER: string[] = [
  'eventCommunications',
  'whatsNew',
  'schoolDetails',
  'profile',
  'importantEvents',
  'weekFixtures',
  'fixtures',
  'clubs',
  'upcomingTournaments',
  'thisWeeksStatistics',
  'housePoints',
  'tipOfTheDay'
];

export const defaultWidgets = DEFAULT_WIDGET_ORDER.slice();

const DASHBOARD_RELEASE = '2026-07-01-muuri-v2';
const LEGACY_STORAGE_KEY = 'dashboardWidgets.v2';
const LEGACY_HIDDEN_KEY = 'dashboardHidden.v1';

interface DashboardLayoutState {
  order: string[];
  hidden: string[];
}

function getDashboardUserKey(user): string {
  return (
    (user && (user.userId || user.id)) ||
    (user && user.profile && (user.profile.id || user.profile.email)) ||
    (user && user.activeSchoolId) ||
    'anonymous'
  );
}

function getDashboardStorageKey(user): string {
  return `dashboardLayout.${DASHBOARD_RELEASE}:${getDashboardUserKey(user)}`;
}

function normaliseOrder(order?: string[]): string[] {
  const seen = new Set<string>();
  const known = (Array.isArray(order) ? order : []).filter(key => {
    if (!DEFAULT_WIDGET_ORDER.includes(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  DEFAULT_WIDGET_ORDER.forEach(key => {
    if (!seen.has(key)) known.push(key);
  });

  return known;
}

function readLegacyLayout(): DashboardLayoutState | null {
  try {
    const rawOrder = localStorage.getItem(LEGACY_STORAGE_KEY) || localStorage.getItem('dashboardWidgets');
    const rawHidden = localStorage.getItem(LEGACY_HIDDEN_KEY);
    const parsedOrder = rawOrder ? JSON.parse(rawOrder) : undefined;
    const parsedHidden = rawHidden ? JSON.parse(rawHidden) : undefined;

    if (!Array.isArray(parsedOrder) && !Array.isArray(parsedHidden)) return null;

    return {
      order: normaliseOrder(Array.isArray(parsedOrder) ? parsedOrder.map(String) : DEFAULT_WIDGET_ORDER),
      hidden: Array.isArray(parsedHidden)
        ? parsedHidden.map(String).filter(key => DEFAULT_WIDGET_ORDER.includes(key))
        : []
    };
  } catch {
    return null;
  }
}

function loadLayoutFromStorage(storageKey: string): DashboardLayoutState {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      const order = normaliseOrder(parsed && Array.isArray(parsed.order) ? parsed.order.map(String) : []);
      const hidden = new Set<string>(
        parsed && Array.isArray(parsed.hidden)
          ? parsed.hidden.map(String).filter(key => DEFAULT_WIDGET_ORDER.includes(key))
          : []
      );

      return { order, hidden: Array.from(hidden) };
    }
  } catch {}

  return readLegacyLayout() || { order: DEFAULT_WIDGET_ORDER.slice(), hidden: [] };
}

function getVisibleWidgets(layout: DashboardLayoutState): string[] {
  const hidden = new Set(layout.hidden);
  return normaliseOrder(layout.order).filter(key => !hidden.has(key));
}

function saveLayoutToStorage(storageKey: string, widgets: string[], hidden: Set<string>) {
  try {
    const visible = widgets.filter(key => DEFAULT_WIDGET_ORDER.includes(key));
    const fullOrder = visible.slice();

    DEFAULT_WIDGET_ORDER.forEach(key => {
      if (!fullOrder.includes(key)) fullOrder.push(key);
    });

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        order: fullOrder,
        hidden: Array.from(hidden).filter(key => DEFAULT_WIDGET_ORDER.includes(key))
      })
    );
  } catch {}
}

const BASE_WIDGET_MIN_H = 220;
const PAGE_SIZE = 5;

const baseCell: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  minHeight: BASE_WIDGET_MIN_H
};

const getWidgetCustomStyle = (_widgetKey: string): React.CSSProperties => {
  return baseCell;
};

interface HouseTotalsRow {
  houseId: string;
  houseName: string;
  totalPoints: number;
  awardCount: number;
}
type SchoolHouseLite = { id: string; name: string; pic?: string; picture?: string; colors?: string[] };

export const Dashboard = ({ user }) => {
  const history = useHistory();
  const avatarSrc =
    user.profile && user.profile.avatar && user.profile.avatar.trim() !== ''
      ? user.profile.avatar
      : '/dist/images/default-avatar.png';

  const formatDayMonth = (value: string | number | Date) =>
    new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  const formatScheduleParts = (value: string | number | Date, isToday = false) => {
    const date = new Date(value);
    return {
      eyebrow: isToday ? 'Today' : date.toLocaleDateString('en-GB', { weekday: 'short' }),
      date: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const pad2 = (n: number) => (n < 10 ? '0' : '') + n;
  const formatDateDMY = (value: string | number | Date) => {
    const d = new Date(value);
    const dd = pad2(d.getDate());
    const mm = pad2(d.getMonth() + 1);
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const todayParam = formatDateDMY(new Date());

  const firstName = (user.profile && user.profile.firstName) || user.firstName;
  const lastName = (user.profile && user.profile.lastName) || user.lastName;
  const email = (user.profile && user.profile.email) || user.email;
  const schoolName = (user.activeSchool && user.activeSchool.name) || user.activeSchoolName;
  const roleName = ROLE_SERVER_TO_CLIENT_MAPPING[user.activeRole] || user.activeRole || 'Role not available';

  const [data, setData] = useState({ roleRequestsCount: 0, invitesCount: 0, tournamentConfirmationRequestsCount: 0 });
  const [fixtures, setFixtures] = useState<any[]>([]);
  const [weekFixtures, setWeekFixtures] = useState<any[]>([]);
  const [clubEvents, setClubEvents] = useState<any[]>([]);
  const [upcomingTournaments, setUpcomingTournaments] = useState<any[]>([]);
  const [houseTotals, setHouseTotals] = useState<HouseTotalsRow[]>([]);
  const [housesById, setHousesById] = useState<Record<string, SchoolHouseLite>>({});
  const [loading, setLoading] = useState(true);
  const emptyCommunications = {
    unread: 0,
    mentions: 0,
    unanswered: 0,
    reported: 0,
    assignedToMe: 0,
    pendingModeration: 0,
    conversationsWithUnread: []
  };
  const [communications, setCommunications] = useState<any>(emptyCommunications);

  const [animatedPoints, setAnimatedPoints] = useState<Record<string, number>>({});
  const rafRef = useRef<number | null>(null);
  const prevPointsRef = useRef<Record<string, number>>({});

  const [weekStatistics, setWeekStatistics] = useState({
    fixtures: 0,
    clubs: 0,
    tournaments: 0,
    fixturePlayers: 0,
    clubPlayers: 0
  });

  const dashboardStorageKey = useMemo(() => getDashboardStorageKey(user), [user]);
  const initialLayoutRef = useRef<DashboardLayoutState | null>(null);
  if (!initialLayoutRef.current) initialLayoutRef.current = loadLayoutFromStorage(dashboardStorageKey);

  const [widgets, setWidgets] = useState<string[]>(() => getVisibleWidgets(initialLayoutRef.current));
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(initialLayoutRef.current.hidden));
  const [boardState, setBoardState] = useState<'pending' | 'ready' | 'error'>('pending');
  const gridRef = useRef<HTMLDivElement | null>(null);
  const hiddenRef = useRef<Set<string>>(hidden);

  const [fixturesPage, setFixturesPage] = useState(0);
  const [weekFixturesPage, setWeekFixturesPage] = useState(0);
  const [clubsPage, setClubsPage] = useState(0);
  const [upcomingTournamentsPage, setUpcomingTournamentsPage] = useState(0);

  useEffect(() => {
    hiddenRef.current = hidden;
    saveLayoutToStorage(dashboardStorageKey, widgets, hidden);
  }, [dashboardStorageKey, widgets, hidden]);

  useEffect(() => {
    const nextLayout = loadLayoutFromStorage(dashboardStorageKey);
    setWidgets(getVisibleWidgets(nextLayout));
    setHidden(new Set(nextLayout.hidden));
  }, [dashboardStorageKey]);

  const dragKeyRef = useRef<string | null>(null);
  const canStartDragRef = useRef(false);

  useEffect(() => {
    const gridEl = gridRef.current;
    if (!gridEl) return;

    setBoardState('ready');
    gridEl.dataset.dashboardLayout = 'native';
    gridEl.classList.add('dashboard-widget-list--native');

    return () => {
      gridEl.classList.remove('dashboard-widget-list--native', 'dashboard-widget-list--dragging');
      delete gridEl.dataset.dashboardLayout;
    };
  }, []);

  const onDashboardItemPointerDown = (event, widgetKey: string) => {
    const target = event.target as HTMLElement;
    canStartDragRef.current = Boolean(
      target.closest('.widget-headerMain') && !target.closest('button, a, input, select, textarea, [data-no-drag]')
    );

    if (canStartDragRef.current) {
      dragKeyRef.current = widgetKey;
    }
  };

  const onDashboardItemDragStart = (event, widgetKey: string) => {
    if (!canStartDragRef.current) {
      event.preventDefault();
      return;
    }

    dragKeyRef.current = widgetKey;
    event.currentTarget.classList.add('dashboard-item--dragging');
    if (gridRef.current) gridRef.current.classList.add('dashboard-widget-list--dragging');

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', widgetKey);
  };

  const onDashboardItemDragOver = (event, overKey: string) => {
    const activeKey = dragKeyRef.current;
    if (!activeKey || activeKey === overKey) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    setWidgets(current => {
      const fromIndex = current.indexOf(activeKey);
      const toIndex = current.indexOf(overKey);

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return current;

      const next = current.slice();
      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, activeKey);
      return next;
    });
  };

  const onDashboardItemDragEnd = event => {
    event.currentTarget.classList.remove('dashboard-item--dragging');
    if (gridRef.current) gridRef.current.classList.remove('dashboard-widget-list--dragging');
    dragKeyRef.current = null;
    canStartDragRef.current = false;
  };

  const availableWidgets = React.useMemo(() => defaultWidgets.filter(w => !widgets.includes(w)), [widgets]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);

      try {
        const [dashboardData, communicationsData] = await Promise.all([
          getSchoolDashboard(user),
          getEventCommunicationsDashboard(user)
        ]);

        if (!mounted) return;

        const counts = dashboardData.counts || {};
        setCommunications(communicationsData);
        const weekStats = dashboardData.weekStatistics || {};

        setData({
          roleRequestsCount: Number(counts.roleRequestsCount) || 0,
          invitesCount: Number(counts.invitesCount) || 0,
          tournamentConfirmationRequestsCount: Number(counts.tournamentConfirmationRequestsCount) || 0
        });

        setFixtures(dashboardData.fixtures || []);
        setClubEvents(dashboardData.clubEvents || []);
        setWeekFixtures(dashboardData.weekFixtures || []);
        setUpcomingTournaments(dashboardData.upcomingTournaments || []);

        setWeekStatistics({
          fixtures: Number(weekStats.fixtures) || 0,
          clubs: Number(weekStats.clubs) || 0,
          tournaments: Number(weekStats.tournaments) || 0,
          fixturePlayers: Number(weekStats.fixturePlayers) || 0,
          clubPlayers: Number(weekStats.clubPlayers) || 0
        });

        const totals = dashboardData.houseTotals || [];
        totals.sort((a, b) => b.totalPoints - a.totalPoints);
        setHouseTotals(totals);

        const byId = (dashboardData.houses || []).reduce((m, h) => {
          m[h.id] = h as SchoolHouseLite;
          return m;
        }, {} as Record<string, SchoolHouseLite>);

        setHousesById(byId);
      } catch (error) {
        if (mounted) console.error('Error fetching dashboard data:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!houseTotals || houseTotals.length === 0) {
      setAnimatedPoints({});
      prevPointsRef.current = {};
      return;
    }
    const targets = houseTotals.reduce((acc, h) => {
      acc[h.houseId] = h.totalPoints;
      return acc;
    }, {} as Record<string, number>);

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setAnimatedPoints(targets);
      prevPointsRef.current = targets;
      return;
    }

    const startVals: Record<string, number> = {};
    for (const id of Object.keys(targets)) startVals[id] = prevPointsRef.current[id] ?? 0;

    const duration = 2000;
    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = easeOutCubic(t);
      const next: Record<string, number> = {};
      for (const id in targets) {
        const from = startVals[id] ?? 0;
        const to = targets[id];
        next[id] = Math.round(from + (to - from) * eased);
      }
      setAnimatedPoints(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevPointsRef.current = targets;
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [houseTotals]);

  useEffect(() => {
    const fixturesPages = Math.max(1, Math.ceil(fixtures.length / PAGE_SIZE));
    const weekFixturesPages = Math.max(1, Math.ceil(weekFixtures.length / PAGE_SIZE));
    const clubsPages = Math.max(1, Math.ceil(clubEvents.length / PAGE_SIZE));
    const tournamentsPages = Math.max(1, Math.ceil(upcomingTournaments.length / PAGE_SIZE));

    if (fixturesPage > fixturesPages - 1) setFixturesPage(0);
    if (weekFixturesPage > weekFixturesPages - 1) setWeekFixturesPage(0);
    if (clubsPage > clubsPages - 1) setClubsPage(0);
    if (upcomingTournamentsPage > tournamentsPages - 1) setUpcomingTournamentsPage(0);
  }, [
    fixtures,
    weekFixtures,
    clubEvents,
    upcomingTournaments,
    fixturesPage,
    weekFixturesPage,
    clubsPage,
    upcomingTournamentsPage
  ]);

  const removeWidget = (key: string) => {
    setWidgets(prev => prev.filter(k => k !== key));
    setHidden(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const addWidget = (key: string) => {
    setWidgets(prev => (prev.includes(key) ? prev : [...prev, key]));
    setHidden(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  if (loading) return <div>Loading...</div>;

  const school = user.activeSchool;
  const getHouseLogo = (houseId: string) =>
    housesById[houseId]?.pic ||
    housesById[houseId]?.picture ||
    school?.pic ||
    '/dist/images/empty_pic_uploader_box.png';
  const getHouseAccent = (houseId: string) => {
    const c = housesById[houseId]?.colors?.[0];
    return c && c.trim() ? c : undefined;
  };
  const getHouseName = (houseId: string, fallback?: string) => housesById[houseId]?.name || fallback || '';
  const nf = new Intl.NumberFormat();

  const getPagedItems = <T,>(items: T[], page: number): T[] => {
    const start = page * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  };

  const renderPager = (totalItems: number, page: number, setPage: (next: number) => void): React.ReactNode => {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    if (totalPages <= 1) return null;

    const canPrev = page > 0;
    const canNext = page < totalPages - 1;

    return (
      <div className="dashboardPager">
        <button
          type="button"
          className="dashboardPagerBtn"
          disabled={!canPrev}
          onClick={() => canPrev && setPage(page - 1)}
        >
          ‹
        </button>

        <span className="dashboardPagerText">
          {page + 1} / {totalPages}
        </span>

        <button
          type="button"
          className="dashboardPagerBtn"
          disabled={!canNext}
          onClick={() => canNext && setPage(page + 1)}
        >
          ›
        </button>
      </div>
    );
  };

  const renderStatIcon = (iconKey: string): React.ReactNode => {
    switch (iconKey) {
      case 'fixtures':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3v3M16 3v3M4 9h16" />
            <rect x="4" y="5" width="16" height="15" rx="3" ry="3" />
          </svg>
        );

      case 'studentsFixtures':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15.5 19c0-2.2-1.8-4-4-4h-3c-2.2 0-4 1.8-4 4" />
            <circle cx="10" cy="8" r="3" />
            <path d="M16 11l2 2 4-4" />
          </svg>
        );

      case 'clubs':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 4l2.2 4.5L19 10l-3.5 3.4.8 4.8-4.3-2.3-4.3 2.3.8-4.8L5 10l4.8-1.5L12 4z" />
          </svg>
        );

      case 'studentsClubs':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="9" cy="8" r="3" />
            <circle cx="16.5" cy="9" r="2.5" />
            <path d="M4 19c0-2.2 2.2-4 5-4s5 1.8 5 4" />
            <path d="M14 19c.2-1.6 1.8-2.8 3.8-2.8 1.4 0 2.6.5 3.2 1.4" />
          </svg>
        );

      case 'tournaments':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
            <path d="M8 5H5a2 2 0 0 0 2 4h1" />
            <path d="M16 5h3a2 2 0 0 1-2 4h-1" />
            <path d="M12 11v4" />
            <path d="M9 20h6" />
            <path d="M8 20h8" />
          </svg>
        );

      default:
        return null;
    }
  };

  const communicationsStats = communications.allSchool
    ? [
        ['Unread (My inbox)', communications.personal.unread, 'UNREAD'],
        ['Needs reply', communications.personal.unanswered, 'UNANSWERED'],
        ['Assigned to you', communications.personal.assignedToMe, 'ASSIGNED'],
        ['Pending', communications.allSchool.pendingModeration, 'PENDING'],
        ['Reported', communications.allSchool.reported, 'REPORTED']
      ]
    : [
        ['Unread (My inbox)', communications.unread, 'UNREAD'],
        ['Needs reply', communications.unanswered, 'UNANSWERED'],
        ['Assigned to you', communications.assignedToMe, 'ASSIGNED'],
        ['Pending', communications.pendingModeration, 'PENDING'],
        ['Reported', communications.reported, 'REPORTED']
      ];
  const eventCommunicationsAvailable =
    !user.activeSchool || (user.activeSchool as any).eventCommunicationsAvailable !== false;
  const widgetContentMap = {
    eventCommunications: (
      <Widget title="Chats" onRemove={() => removeWidget('eventCommunications')}>
        {eventCommunicationsAvailable ? (
          <div className="statistics-widget eventCommunications-widget">
            <ul className="statistics-list stats-compact">
              {communicationsStats.map(([label, value, filter], index) => (
                <React.Fragment key={String(label)}>
                  {index === 3 && <li className="eventCommunications-widget__group">Moderation</li>}
                  <li>
                    <button
                      type="button"
                      className={`stat-row ${Number(value) === 0 ? 'is-muted' : ''} ${
                        filter === 'PENDING' ? 'is-pending' : filter === 'REPORTED' ? 'is-reported' : ''
                      }`}
                      onClick={() =>
                        history.push(
                          `/eventCommunications?filter=${filter}${
                            filter === 'PENDING' || filter === 'REPORTED' ? '&scope=ALL_SCHOOL' : ''
                          }`
                        )
                      }
                    >
                      <span className="stat-main">
                        <span className="field-label">{label}</span>
                      </span>
                      <span className="stat-value">{value}</span>
                    </button>
                  </li>
                </React.Fragment>
              ))}
            </ul>
            <button type="button" className="primary-cta-btn" onClick={() => history.push('/eventCommunications')}>
              Open chats
            </button>
          </div>
        ) : (
          <div className="statistics-widget eventCommunications-widget eventCommunications-widget--unavailable">
            <p>Chats are not enabled for your school yet.</p>
            <a className="primary-cta-btn" href="mailto:support@squadintouch.com?subject=Enable%20Chats">
              Contact support
            </a>
          </div>
        )}
      </Widget>
    ),
    whatsNew: <WhatsNewWidget onRemove={() => removeWidget('whatsNew')} />,
    schoolDetails: school && (
      <Widget title="School Details" onRemove={() => removeWidget('schoolDetails')}>
        <div className="school-details-widget school-card-widget">
          <div className="school-card-top">
            <div className="school-logo-wrapper school-logo-wrapper--large">
              <img src={school.pic || '/dist/images/empty_pic_uploader_box.png'} alt={school.name} />
            </div>

            <div className="school-card-main">
              <div className="school-card-eyebrow">Active school</div>

              <h4 className="school-card-title">
                <Link to="/school/settings/base">{school.name}</Link>
              </h4>

              <div className="school-card-badges">
                {school.postcode?.postcode ? (
                  <span className="school-card-badge">{school.postcode.postcode}</span>
                ) : null}

                {school.domain ? (
                  <span className="school-card-badge school-card-badge--subtle">{school.domain}</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="school-card-body">
            <div className="school-card-row">
              <span className="school-card-label">Address</span>
              <span className="school-card-value">{school.address || 'No address added yet.'}</span>
            </div>

            <div className="school-card-actions">
              <Link to="/school/settings/base" className="dashboard-actionBtn dashboard-actionBtn--secondary">
                Edit school details
              </Link>

              {school.domain ? (
                <a
                  className="dashboard-iconBtn dashboard-iconBtn--external"
                  href={`https://${school.domain}.squadintouch.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open public website"
                  title="Open public website"
                >
                  <span className="school-card-actionIcon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M14 3h7v7" />
                      <path d="M10 14 21 3" />
                      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                    </svg>
                  </span>
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </Widget>
    ),
    profile: (
      <Widget title="Profile" onRemove={() => removeWidget('profile')}>
        <div className="profile-widget profile-card-widget">
          <div className="profile-card-top">
            <div className="profile-avatar profile-avatar--large">
              <img src={avatarSrc} alt={`${firstName} ${lastName}`} />
            </div>

            <div className="profile-card-main">
              <div className="profile-card-eyebrow">Signed in as</div>

              <h4 className="profile-card-title">
                <Link to="/profile/summary">{`${firstName} ${lastName}`}</Link>
              </h4>

              <div className="profile-card-badges">
                <span className="profile-card-badge">{roleName}</span>
                {schoolName ? (
                  <span className="profile-card-badge profile-card-badge--subtle">{schoolName}</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="profile-card-body">
            <div className="profile-card-row">
              <span className="profile-card-label">Email</span>
              <span className="profile-card-value">{email || '—'}</span>
            </div>

            <div className="profile-card-actions">
              <Link to="/profile/summary" className="dashboard-actionBtn dashboard-actionBtn--secondary">
                Edit profile
              </Link>
            </div>
          </div>
        </div>
      </Widget>
    ),
    housePoints: (
      <Widget title="House Points" onRemove={() => removeWidget('housePoints')}>
        <div className="house-points-widget">
          {houseTotals.length === 0 ? (
            <div className="empty-state">No points recorded yet.</div>
          ) : (
            (() => {
              const maxPts = Math.max(...houseTotals.map(h => h.totalPoints), 1);
              return (
                <ul className="house-points-list">
                  {(() => {
                    const hasLeader = houseTotals.some(h => Number(h.totalPoints) > 0);

                    return houseTotals.map((h, idx) => {
                      const currentPts = animatedPoints[h.houseId] ?? 0;
                      const pct = Math.round((currentPts / maxPts) * 100);
                      const isLeader = hasLeader && idx === 0;

                      return (
                        <li className={`house-card${isLeader ? ' house-card--leader' : ''}`} key={h.houseId}>
                          <div className="house-rank">{idx + 1}</div>
                          <div className="house-logo">
                            <img src={getHouseLogo(h.houseId)} alt={getHouseName(h.houseId, h.houseName)} />
                          </div>
                          <div className="house-meta">
                            <div className="house-name" title={getHouseName(h.houseId, h.houseName)}>
                              {getHouseName(h.houseId, h.houseName)}
                            </div>
                            <div className="house-bar">
                              <div
                                className="house-bar-fill"
                                style={{ width: `${pct}%`, background: getHouseAccent(h.houseId) || undefined }}
                                aria-label={`${getHouseName(h.houseId, h.houseName)} progress`}
                              />
                            </div>
                          </div>
                          <div className="house-points">{nf.format(currentPts)} pts</div>
                        </li>
                      );
                    });
                  })()}
                </ul>
              );
            })()
          )}
        </div>
      </Widget>
    ),
    importantEvents: (
      <Widget
        title={<span className="important-title">Important</span>}
        onRemove={() => removeWidget('importantEvents')}
      >
        <div className="important-events-widget important-events-widget--cards">
          {data.roleRequestsCount > 0 || data.invitesCount > 0 || data.tournamentConfirmationRequestsCount > 0 ? (
            <div className="important-events-list">
              {data.roleRequestsCount > 0 && (
                <Link to="/requests" className="important-event-card important-event-card--requests">
                  <span className="important-event-cardIcon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <circle cx="9" cy="8" r="3" />
                      <path d="M4 19c0-2.4 2.2-4 5-4s5 1.6 5 4" />
                      <path d="M15 11l2 2 4-4" />
                    </svg>
                  </span>
                  <span className="important-event-cardBody">
                    <span className="important-event-cardTitle">Pending role requests</span>
                    <span className="important-event-cardMeta">Needs your review</span>
                  </span>
                  <span className="important-event-cardCount">{data.roleRequestsCount}</span>
                </Link>
              )}

              {data.invitesCount > 0 && (
                <Link to="/invites/inbox" className="important-event-card important-event-card--invites">
                  <span className="important-event-cardIcon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <rect x="4" y="6" width="16" height="12" rx="2" />
                      <path d="M5 8l7 5 7-5" />
                    </svg>
                  </span>
                  <span className="important-event-cardBody">
                    <span className="important-event-cardTitle">New invites</span>
                    <span className="important-event-cardMeta">Inbox items waiting for action</span>
                  </span>
                  <span className="important-event-cardCount">{data.invitesCount}</span>
                </Link>
              )}

              {data.tournamentConfirmationRequestsCount > 0 && (
                <Link
                  to="/messages/tournamentConfirmationRequests/inbox"
                  className="important-event-card important-event-card--tournaments"
                >
                  <span className="important-event-cardIcon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
                      <path d="M8 5H5a2 2 0 0 0 2 4h1" />
                      <path d="M16 5h3a2 2 0 0 1-2 4h-1" />
                      <path d="M12 11v4" />
                      <path d="M9 20h6" />
                    </svg>
                  </span>
                  <span className="important-event-cardBody">
                    <span className="important-event-cardTitle">Tournament confirmations</span>
                    <span className="important-event-cardMeta">Event organisers are waiting for a response</span>
                  </span>
                  <span className="important-event-cardCount">{data.tournamentConfirmationRequestsCount}</span>
                </Link>
              )}
            </div>
          ) : (
            <div className="up-to-date-panel">
              <div className="up-to-date-panelIcon">✓</div>
              <div className="up-to-date-panelBody">
                <div className="up-to-date-panelTitle">All caught up</div>
                <div className="up-to-date-panelText">No urgent actions are waiting for you right now.</div>
              </div>
            </div>
          )}
        </div>
      </Widget>
    ),
    fixtures: (
      <Widget title="Today's Fixtures" onRemove={() => removeWidget('fixtures')}>
        <div className="fixtures-widget">
          <div className="fixtures-list-area">
            {fixtures.length > 0 ? (
              <ul className="dashboardEventList dashboardEventList--today">
                {getPagedItems(fixtures, fixturesPage).map(event => {
                  const eventName = getEventNameForSchool(event, user.activeSchool.id);
                  const sportName = String(event?.sport?.name || '').trim();

                  const schedule = formatScheduleParts(event.startTime, true);
                  const cancelled = event.status === 'CANCELED';

                  return (
                    <li key={event.id}>
                      <Link to={`/events/event?id=${event.id}`} className="dashboardScheduleRow">
                        <div className="dashboardScheduleBlock">
                          <span className="dashboardScheduleEyebrow">{schedule.eyebrow}</span>
                          <span className="dashboardScheduleTime">{schedule.time}</span>
                        </div>

                        <div className="dashboardScheduleMain">
                          <div
                            className="dashboardScheduleName"
                            title={eventName}
                            style={{
                              textDecoration: cancelled ? 'line-through' : 'none'
                            }}
                          >
                            {eventName}
                          </div>

                          <div className="dashboardScheduleMeta">
                            {cancelled
                              ? sportName
                                ? `${sportName} • Cancelled`
                                : 'Cancelled fixture'
                              : sportName || 'Fixture'}
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="widget-empty-state">
                <div className="widget-empty-stateTitle">No fixtures today</div>
                <div className="widget-empty-stateText">Your school has no scheduled fixtures for today.</div>
              </div>
            )}
          </div>
          {renderPager(fixtures.length, fixturesPage, setFixturesPage)}
          <div className="fixtures-footer">
            <button
              type="button"
              className="primary-cta-btn"
              onClick={() => history.push(`/events/manager?date=${todayParam}`)}
            >
              Create event
            </button>
          </div>
        </div>
      </Widget>
    ),
    weekFixtures: (
      <Widget title="This Week's Fixtures" onRemove={() => removeWidget('weekFixtures')}>
        <div className="week-fixtures-widget">
          <div className="week-fixtures-list-area">
            <ul className="tournaments-list">
              {weekFixtures.length > 0 ? (
                getPagedItems(weekFixtures, weekFixturesPage).map(event => {
                  const eventName = getEventNameForSchool(event, user.activeSchool.id);
                  const sportName = String(event?.sport?.name || '').trim();
                  const schedule = formatScheduleParts(event.startTime);
                  const cancelled = event.status === 'CANCELED';

                  return (
                    <li key={event.id}>
                      <Link to={`/events/event?id=${event.id}`} className="dashboardScheduleRow">
                        <div className="dashboardScheduleBlock">
                          <span className="dashboardScheduleEyebrow">{schedule.eyebrow}</span>
                          <span className="dashboardScheduleDate">{schedule.date}</span>
                          <span className="dashboardScheduleTime">{schedule.time}</span>
                        </div>

                        <div className="dashboardScheduleMain">
                          <div
                            className="dashboardScheduleName"
                            title={eventName}
                            style={{
                              textDecoration: cancelled ? 'line-through' : 'none'
                            }}
                          >
                            {eventName}
                          </div>

                          <div className="dashboardScheduleMeta">
                            {cancelled
                              ? sportName
                                ? `${sportName} • Cancelled`
                                : 'Cancelled fixture'
                              : sportName || 'Fixture'}
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })
              ) : (
                <div className="widget-empty-state">
                  <div className="widget-empty-stateTitle">No fixtures this week</div>
                  <div className="widget-empty-stateText">
                    Your upcoming school fixtures for this week will appear here.
                  </div>
                </div>
              )}
            </ul>
          </div>
          {renderPager(weekFixtures.length, weekFixturesPage, setWeekFixturesPage)}
          <div className="week-fixtures-footer">
            <button type="button" className="primary-cta-btn" onClick={() => history.push('/calendar')}>
              Open calendar
            </button>
          </div>
        </div>
      </Widget>
    ),
    clubs: (
      <Widget title="Today's Clubs" onRemove={() => removeWidget('clubs')}>
        <div className="clubs-widget">
          <div className="clubs-list-area">
            {clubEvents.length > 0 ? (
              <ul className="tournaments-list">
                {getPagedItems(clubEvents, clubsPage).map((event: any) => {
                  const eventName = getEventNameForSchool(event, user.activeSchool.id);
                  const sportName = String(event?.sport?.name || '').trim();

                  const schedule = formatScheduleParts(event.startTime, true);
                  const cancelled = event.status === 'CANCELED';

                  return (
                    <li key={event.id}>
                      <Link to={`/events/event?id=${event.id}`} className="dashboardScheduleRow">
                        <div className="dashboardScheduleBlock">
                          <span className="dashboardScheduleEyebrow">{schedule.eyebrow}</span>
                          <span className="dashboardScheduleTime">{schedule.time}</span>
                        </div>

                        <div className="dashboardScheduleMain">
                          <div
                            className="dashboardScheduleName"
                            title={eventName}
                            style={{
                              textDecoration: cancelled ? 'line-through' : 'none'
                            }}
                          >
                            {eventName}
                          </div>

                          <div className="dashboardScheduleMeta">
                            {cancelled
                              ? sportName
                                ? `${sportName} • Cancelled`
                                : 'Cancelled club'
                              : sportName || 'Club session'}
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="widget-empty-state">
                <div className="widget-empty-stateTitle">No clubs today</div>
                <div className="widget-empty-stateText">There are no club sessions scheduled for today.</div>
              </div>
            )}
          </div>
          {renderPager(clubEvents.length, clubsPage, setClubsPage)}
          <div className="clubs-footer">
            <button
              type="button"
              className="primary-cta-btn"
              onClick={() => history.push('/clubs?status=ACTIVE&status=DRAFT')}
            >
              Manage clubs
            </button>
          </div>
        </div>
      </Widget>
    ),
    upcomingTournaments: (
      <Widget title="Upcoming Tournaments" onRemove={() => removeWidget('upcomingTournaments')}>
        <div className="upcoming-tournaments-widget">
          <div className="upcoming-tournaments-list-area">
            <ul className="tournaments-list">
              {upcomingTournaments.length > 0 ? (
                getPagedItems(upcomingTournaments, upcomingTournamentsPage).map(tournament => {
                  const tournamentName = tournament.name || 'Unnamed Tournament';
                  const tournamentSport = tournament.sport ? String(tournament.sport.name).trim() : '';
                  const startDate = formatDayMonth(tournament.startTime);
                  const startTime = new Date(tournament.startTime).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  return (
                    <li key={tournament.id}>
                      <Link to={`/tournaments/upcoming`} className="dashboardScheduleRow">
                        <span className="dashboardScheduleBlock">
                          <span className="upcoming-tournament-date">{startDate}</span>
                          <span className="upcoming-tournament-time">{startTime}</span>
                        </span>

                        <span className="dashboardScheduleMain">
                          <span className="upcoming-tournament-name my-ellipsis" title={tournamentName}>
                            {tournamentName}
                          </span>
                          <span className="dashboardScheduleMeta">{tournamentSport || 'Upcoming tournament'}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })
              ) : (
                <li>No upcoming tournaments</li>
              )}
            </ul>
          </div>
          {renderPager(upcomingTournaments.length, upcomingTournamentsPage, setUpcomingTournamentsPage)}
          <div className="upcoming-tournaments-footer">
            <button type="button" className="primary-cta-btn" onClick={() => history.push('/tournaments/upcoming')}>
              Manage tournaments
            </button>
          </div>
        </div>
      </Widget>
    ),
    thisWeeksStatistics: (
      <Widget title="This Week's Statistics" onRemove={() => removeWidget('thisWeeksStatistics')}>
        <div className="statistics-widget">
          {(() => {
            const stats = [
              { kind: 'fixtures', label: 'Fixtures', value: Number(weekStatistics.fixtures) || 0, iconKey: 'fixtures' },
              {
                kind: 'students-fix',
                label: 'Students involved in fixtures',
                value: Number(weekStatistics.fixturePlayers) || 0,
                iconKey: 'studentsFixtures'
              },
              { kind: 'sessions', label: 'Club Sessions', value: Number(weekStatistics.clubs) || 0, iconKey: 'clubs' },
              {
                kind: 'students-club',
                label: 'Students involved in Clubs',
                value: Number(weekStatistics.clubPlayers) || 0,
                iconKey: 'studentsClubs'
              },
              {
                kind: 'tournaments',
                label: 'Tournaments',
                value: Number(weekStatistics.tournaments) || 0,
                iconKey: 'tournaments'
              }
            ];

            return (
              <ul className="statistics-list stats-compact">
                {stats.map(s => (
                  <li key={s.kind} className={`stat-row${s.value === 0 ? ' is-zero' : ''}`} data-kind={s.kind}>
                    <span className="stat-icon" aria-hidden="true">
                      {renderStatIcon(s.iconKey)}
                    </span>

                    <span className="stat-main">
                      <span className="field-label">{s.label}</span>
                    </span>

                    <span className="stat-value" aria-label={`${s.label}: ${s.value}`}>
                      {s.value}
                    </span>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
      </Widget>
    ),
    tipOfTheDay: <TipOfTheDayWidget onRemove={() => removeWidget('tipOfTheDay')} />
  };

  return (
    <div className="dashboard-viewport">
      <div className="dashboard">
        <header className="dashboard-intro">
          <div>
            <div className="dashboard-introTitle">Dashboard</div>
            <div className="dashboard-introSubtitle">
              Welcome back{firstName ? `, ${firstName}` : ''}. Drag widgets to rearrange, × to hide.
            </div>
          </div>
        </header>

        <TournamentAvailabilityInbox user={user} compact />
        <div className="dashboard-widget-list" ref={gridRef} data-dashboard-layout={boardState}>
          {widgets.map(widgetKey => {
            const widget = widgetContentMap[widgetKey] as React.ReactElement;
            if (!widget) return null;

            return (
              <div
                key={widgetKey}
                className="dashboard-item"
                data-widget-key={widgetKey}
                draggable={true}
                onMouseDown={event => onDashboardItemPointerDown(event, widgetKey)}
                onTouchStart={event => onDashboardItemPointerDown(event, widgetKey)}
                onDragStart={event => onDashboardItemDragStart(event, widgetKey)}
                onDragOver={event => onDashboardItemDragOver(event, widgetKey)}
                onDragEnd={onDashboardItemDragEnd}
                onDrop={event => event.preventDefault()}
              >
                <div style={getWidgetCustomStyle(widgetKey)}>
                  {React.cloneElement(widget, {
                    dragHandleProps: {
                      title: 'Drag to reorder',
                      'aria-label': 'Drag widget'
                    }
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {availableWidgets.length > 0 && (
          <div className="widget-library">
            <div className="widget-libraryHeader">
              <div className="widget-libraryTitle">Add widgets</div>
              <div className="widget-librarySubtitle">Bring back any dashboard blocks you’ve removed.</div>
            </div>

            <div className="widget-libraryList">
              {!widgets.includes('eventCommunications') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('eventCommunications')}>
                  Chats
                </button>
              )}

              {!widgets.includes('whatsNew') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('whatsNew')}>
                  What’s New
                </button>
              )}

              {!widgets.includes('schoolDetails') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('schoolDetails')}>
                  School Details
                </button>
              )}

              {!widgets.includes('profile') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('profile')}>
                  Profile
                </button>
              )}

              {!widgets.includes('importantEvents') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('importantEvents')}>
                  Important Events
                </button>
              )}

              {!widgets.includes('housePoints') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('housePoints')}>
                  House Points
                </button>
              )}

              {!widgets.includes('fixtures') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('fixtures')}>
                  Today's Fixtures
                </button>
              )}

              {!widgets.includes('weekFixtures') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('weekFixtures')}>
                  Week Fixtures
                </button>
              )}

              {!widgets.includes('clubs') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('clubs')}>
                  Today's Clubs
                </button>
              )}

              {!widgets.includes('upcomingTournaments') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('upcomingTournaments')}>
                  Upcoming Tournaments
                </button>
              )}

              {!widgets.includes('thisWeeksStatistics') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('thisWeeksStatistics')}>
                  This Week's Statistics
                </button>
              )}

              {!widgets.includes('tipOfTheDay') && (
                <button type="button" className="widget-libraryBtn" onClick={() => addWidget('tipOfTheDay')}>
                  Tip of the Day
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
